/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { ICompanyFile } from '@leedpulse-monorepo/interfaces';
import { Authentication } from '@leedpulse-monorepo/classes';
import getCompanyFiles from '../../../../domain/use_cases/companies/getCompanyFiles';

export const COMPANY_FILES_FETCH_LIMIT = 20;

const CompanyFilesContext = createContext({
  count: 0,
  hitsPerPage: COMPANY_FILES_FETCH_LIMIT,
  items: [] as ICompanyFile[],
  scopedCompanyId: null as string | null,
  fetch: (_: string | null, __: Authentication | null, ___?: number, ____?: number) => {},
  reset: () => {},
  status: ContextStatus.IDLE,
});

export const CompanyFilesProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(COMPANY_FILES_FETCH_LIMIT);
  const [items, setItems] = useState([] as ICompanyFile[]);
  const [scopedCompanyId, setScopedCompanyId] = useState(null as string | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const fetch = async (
    companyId: string | null,
    auth: Authentication | null,
    page = 1,
    pageSize?: number
  ) => {
    const effectiveLimit = pageSize ?? hitsPerPage;
    if (pageSize !== undefined) setHitsPerPage(pageSize);
    setScopedCompanyId(companyId);
    setStatus(ContextStatus.LOADING);
    try {
      if (!companyId) {
        setItems([]);
        setCount(0);
        setStatus(ContextStatus.SUCCESS);
        return;
      }
      const useCaseResponse = await getCompanyFiles(companyId, effectiveLimit, page, auth);
      setItems(useCaseResponse.items);
      setCount(useCaseResponse.totalCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(COMPANY_FILES_FETCH_LIMIT);
    setItems([]);
    setScopedCompanyId(null);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <CompanyFilesContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        scopedCompanyId,
        fetch,
        reset,
        status,
      }}
    >
      {children}
    </CompanyFilesContext.Provider>
  );
};

export default CompanyFilesContext;
